import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import BoilerCard from '../components/BoilerCard';
import { useBoilerSocket } from '../hooks/useSocket';
import EmptyState, { BoilerEmptyIcon, SearchEmptyIcon } from '../components/EmptyState';

function BoilerSocketListener({ boilerId, onUpdate }) {
  useBoilerSocket(boilerId, onUpdate);
  return null;
}

export default function DashboardPage() {
  const [boilers, setBoilers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/boilers').then((res) => setBoilers(res.data)).finally(() => setLoading(false));
  }, []);

  const handleBoilerUpdate = useCallback((data) => {
    setBoilers((prev) =>
      prev.map((boiler) =>
        boiler.id === data.boilerId
          ? { ...boiler, [data.field]: data.value, last_seen: data.timestamp }
          : boiler
      )
    );
  }, []);

  const OFFLINE_MS = 2 * 60 * 1000;
  const isOnline = (b) => b.last_seen && (Date.now() - new Date(b.last_seen).getTime()) < OFFLINE_MS;

  const filtered = boilers
    .filter((b) =>
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      (b.location || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.owner_name || '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => (isOnline(b) ? 1 : 0) - (isOnline(a) ? 1 : 0));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Мои котельные</h1>
          <p className="text-slate-400 text-sm mt-0.5">{boilers.length} устройств</p>
        </div>
      </div>

      {boilers.length > 3 && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Поиск по названию или адресу..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>
      )}

      {boilers.map((b) => (
        <BoilerSocketListener key={b.id} boilerId={b.id} onUpdate={handleBoilerUpdate} />
      ))}

      {boilers.length === 0 ? (
        <EmptyState
          icon={<BoilerEmptyIcon />}
          title="Нет котельных"
          description="Котельные появятся здесь после того, как администратор добавит их в систему"
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<SearchEmptyIcon />}
          title="Ничего не найдено"
          description="Попробуйте изменить поисковый запрос"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((boiler) => (
            <BoilerCard key={boiler.id} boiler={boiler} />
          ))}
        </div>
      )}
    </div>
  );
}
